import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Upload, FileText, Image, Trash2, Download, Eye, Loader2, File, FileCheck, Lock, Search } from 'lucide-react';
import { toast } from 'sonner';
import SitePhotoAnnotator from '@/components/common/SitePhotoAnnotator';

// Alian: Site Photos con anotación (círculo/marca) — categoría por tipo de entidad.
const SITE_PHOTO_CATEGORY = { job: 'site_photos', project: 'photos' };

const DOC_CATEGORIES = {
  project: [
    { value: 'contract', label: 'Signed Contract' },
    { value: 'site_map', label: 'Site Map / Plans' },
    { value: 'well_application', label: 'Well Application PDF' },
    { value: 'approved_permit_doh', label: 'Approved Permit / DOH' },
    { value: 'photos', label: 'Site Photos' },
    { value: 'other', label: 'Other' },
  ],
  job: [
    { value: 'completion_report', label: 'Completion Report PDF' },
    { value: 'site_photos', label: 'Site Photos' },
    { value: 'inspection', label: 'Inspection Report' },
    { value: 'well_application', label: 'Well Application PDF' },
    { value: 'approved_permit_doh', label: 'Approved Permit / DOH' },
    { value: 'other', label: 'Other' },
  ],
  permit: [
    { value: 'approved_permit_doh', label: 'Approved Permit / DOH' },
    { value: 'well_application_ext', label: 'Well Application (External)' },
    { value: 'completion_report_ext', label: 'Completion Report (External)' },
    { value: 'approval_letter', label: 'Approval Letter' },
    { value: 'site_map', label: 'Maps / Plans' },
    { value: 'other', label: 'Other' },
  ],
};

const FILE_ICONS = {
  'application/pdf': FileText,
  'image/png': Image,
  'image/jpeg': Image,
  'image/jpg': Image,
  'image/webp': Image,
  default: File,
};

function getFileIcon(mime) {
  const Icon = FILE_ICONS[mime] || FILE_ICONS.default;
  return Icon;
}

function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsSection({ entityType, entityId, onCountChange }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('other');
  const [docName, setDocName] = useState('');
  const [search, setSearch] = useState('');
  const fileInputRef = useRef(null);
  const categories = DOC_CATEGORIES[entityType] || DOC_CATEGORIES.project;
  const visibleDocuments = documents.filter(d =>
    !search.trim() || (d.description || d.original_filename || d.filename || '').toLowerCase().includes(search.trim().toLowerCase())
  );

  // Fetch documents on mount
  useEffect(() => {
    fetchDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  async function fetchDocuments() {
    try {
      const res = await api.get(`/files?entity_type=${entityType}&entity_id=${entityId}`);
      setDocuments(res.data || []);
      onCountChange && onCountChange((res.data || []).length);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }

  async function handleUpload(e) {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        formData.append('category', category);
        if (docName.trim()) formData.append('description', docName.trim());
        await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(t('documents.uploaded', { defaultValue: 'Document(s) uploaded' }));
      setDocName('');
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDelete(docId) {
    if (!window.confirm(t('documents.confirmDelete', { defaultValue: 'Delete this document?' }))) return;
    try {
      await api.delete(`/files/${docId}`);
      toast.success(t('documents.deleted', { defaultValue: 'Document deleted' }));
      fetchDocuments();
    } catch {
      toast.error('Error deleting');
    }
  }

  function handleDownload(doc) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/download/${doc.id}`;
    window.open(url, '_blank');
  }

  // Legacy fix (Alian): never force a .zip download for multiple documents —
  // each doc already has its own Ver/Descargar button above; this covers the
  // "combinarlos en un solo PDF" alternative when there are 2+ PDFs.
  const pdfCount = documents.filter(d => d.content_type === 'application/pdf').length;
  function handleDownloadCombined() {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/combined-pdf?entity_type=${entityType}&entity_id=${entityId}`;
    window.open(url, '_blank');
  }

  return (
    <Card className="bg-white border-gray-100">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-[#003366]" />
            <h3 className="text-sm font-semibold text-gray-800" data-testid="documents-section-title">
              {t('documents.title', { defaultValue: 'Documents' })}
            </h3>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{documents.length}</span>
          </div>
          <div className="flex items-center gap-2">
            {SITE_PHOTO_CATEGORY[entityType] && (
              <SitePhotoAnnotator
                entityType={entityType}
                entityId={entityId}
                category={SITE_PHOTO_CATEGORY[entityType]}
                onUploaded={fetchDocuments}
              />
            )}
            {pdfCount >= 2 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadCombined}
                className="h-8 text-xs border-[#003366]/20 text-[#003366]"
                data-testid="doc-download-combined-btn"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                {t('documents.downloadCombined', { defaultValue: 'Download combined PDF' })}
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        {documents.length > 0 && (
          <div className="relative mb-3 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('documents.searchPlaceholder', { defaultValue: 'Search files…' })} className="h-8 pl-8 text-xs" data-testid="documents-search-input" />
          </div>
        )}

        {/* Upload Area */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-dashed border-gray-200 flex-wrap">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 w-48 text-xs" data-testid="doc-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={docName}
            onChange={e => setDocName(e.target.value)}
            placeholder={t('documents.namePlaceholder', { defaultValue: 'Document name (optional) — e.g. Approved Permit App.' })}
            className="h-8 text-xs flex-1 min-w-[220px]"
            data-testid="doc-name-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            className="hidden"
            data-testid="doc-file-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-8 text-xs border-[#003366]/20 text-[#003366]"
            data-testid="doc-upload-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            {uploading ? t('documents.uploading', { defaultValue: 'Uploading...' }) : t('documents.uploadBtn', { defaultValue: 'Upload' })}
          </Button>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="text-center py-6 text-gray-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading...
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-6" data-testid="no-documents">
            <FileText className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">{t('documents.empty', { defaultValue: 'No documents attached yet' })}</p>
          </div>
        ) : visibleDocuments.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4" data-testid="documents-search-empty">{t('documents.noSearchResults', { defaultValue: 'No files match your search' })}</p>
        ) : (
          <div className="space-y-2">
            {visibleDocuments.map(doc => {
              const IconComp = getFileIcon(doc.content_type);
              return (
                <div key={doc.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors group" data-testid={`doc-item-${doc.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <IconComp className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{doc.description || doc.original_filename || doc.filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-gray-400">{formatSize(doc.file_size)}</span>
                      {doc.read_only ? (
                        <span className="text-[10px] flex items-center gap-1 bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded" data-testid={`doc-readonly-badge-${doc.id}`}>
                          <Lock className="w-2.5 h-2.5" /> {t('documents.autoSent', { defaultValue: 'Auto-sent' })}
                        </span>
                      ) : (
                        doc.category && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{doc.category}</span>
                      )}
                      {doc.uploaded_at && <span className="text-[10px] text-gray-400">{new Date(doc.uploaded_at).toLocaleDateString()}</span>}
                    </div>
                    {doc.description && <p className="text-[10px] text-gray-400 mt-0.5 truncate">{doc.original_filename || doc.filename}</p>}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {doc.content_type?.startsWith('image/') && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDownload(doc)} data-testid={`doc-preview-${doc.id}`}>
                        <Eye className="w-3.5 h-3.5 text-gray-500" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDownload(doc)} data-testid={`doc-download-${doc.id}`}>
                      <Download className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(doc.id)} data-testid={`doc-delete-${doc.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
